import React from 'react';
import {
  Flex,
  Heading,
  Text,
  DataTable,
  BarChart,
  LineChart,
  PieChart,
  ChartEmptyState,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { RenderType } from './types';

export type DynamicResult = {
  renderType: RenderType;
  title?: string;
  xKey?: string;
  yKey?: string;
  nameKey?: string;
  data: Record<string, unknown>[];
};

export function DynamicResultRenderer({ result }: { result: DynamicResult }) {
  const data = result.data ?? [];

  if (data.length === 0) {
    return <ChartEmptyState message="No results" description="The query returned no rows." />;
  }

  switch (result.renderType) {
    case 'bar_chart':
      return (
        <BarChart
          data={data}
          xDataKey={result.xKey ?? Object.keys(data[0])[0]}
          bars={[{ dataKey: result.yKey ?? Object.keys(data[0])[1], name: result.yKey }]}
          height={320}
        />
      );

    case 'line_chart':
      return (
        <LineChart
          data={data}
          xDataKey={result.xKey ?? Object.keys(data[0])[0]}
          lines={[{ dataKey: result.yKey ?? Object.keys(data[0])[1], name: result.yKey }]}
          height={320}
        />
      );

    case 'pie_chart':
      return (
        <PieChart
          data={data}
          dataKey={result.yKey ?? Object.keys(data[0])[1]}
          nameKey={result.nameKey ?? Object.keys(data[0])[0]}
          height={320}
          outerRadius={120}
        />
      );

    case 'big_number': {
      const valueKey = result.yKey ?? Object.keys(data[0])[0];
      const labelKey = result.xKey ?? Object.keys(data[0]).find((k) => k !== valueKey);
      return (
        <div className={data.length > 1 ? 'grid grid-cols-2 md:grid-cols-3 gap-4 py-4' : 'py-8'}>
          {data.map((row, i) => {
            const value = row[valueKey];
            const label = labelKey ? String(row[labelKey] ?? '') : null;
            return (
              <Flex
                key={i}
                direction="column"
                align="center"
                className={data.length === 1 ? '' : 'py-4'}
              >
                {label && (
                  <Text className="text-muted-foreground mb-1 font-mono text-sm">{label}</Text>
                )}
                <Heading
                  level={data.length === 1 ? 1 : 3}
                  className={
                    data.length === 1
                      ? 'text-5xl font-bold tabular-nums'
                      : 'text-3xl font-bold tabular-nums'
                  }
                >
                  {typeof value === 'number' ? value.toLocaleString() : String(value ?? '—')}
                </Heading>
                <Text className="text-muted-foreground mt-1 text-xs">{valueKey}</Text>
              </Flex>
            );
          })}
        </div>
      );
    }

    case 'table':
    default: {
      const keys = Object.keys(data[0]);
      const columns: ColumnDef<StockFeatures, Record<string, unknown>>[] = keys.map((key) => ({
        accessorKey: key,
        header: key,
        cell: ({ row }) => {
          const val = row.original[key];
          if (val === null || val === undefined) {
            return <Text className="text-muted-foreground">—</Text>;
          }
          if (typeof val === 'object') {
            return (
              <Text className="font-mono text-xs" title={JSON.stringify(val, null, 2)}>
                {JSON.stringify(val).slice(0, 80)}
              </Text>
            );
          }
          return <Text className="text-sm">{String(val)}</Text>;
        },
      }));

      return <DataTable columns={columns} data={data} stickyHeader />;
    }
  }
}

export function SqlDisplay({ sql }: { sql: string }) {
  return (
    <div className="rounded-lg bg-muted/50 border border-border p-4 overflow-x-auto">
      <Text size="xs" className="text-muted-foreground mb-1 block font-medium">
        Generated SQL
      </Text>
      <pre className="font-mono text-sm whitespace-pre-wrap text-foreground">{sql}</pre>
    </div>
  );
}
